import { AlertTriangle } from 'lucide-react';
import { formatPence } from '../api/balance';

interface InsufficientBalanceProps {
  currentBalancePence: number;
  requiredAmountPence: number;
  shortfall: number;
  onAddFunds: () => void;
}

/**
 * Warning component shown when user has insufficient balance for a purchase.
 * Displays current balance vs required amount and an "Add Funds" link.
 */
export function InsufficientBalance({
  currentBalancePence,
  requiredAmountPence,
  shortfall,
  onAddFunds,
}: InsufficientBalanceProps) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-500" />
        <div className="flex-1">
          <p className="text-sm font-medium text-red-800">Insufficient Balance</p>
          <div className="mt-2 text-sm text-red-700">
            <div className="flex justify-between">
              <span>Current balance:</span>
              <span className="font-medium">{formatPence(currentBalancePence)}</span>
            </div>
            <div className="flex justify-between">
              <span>Required:</span>
              <span className="font-medium">{formatPence(requiredAmountPence)}</span>
            </div>
            <div className="mt-1 flex justify-between border-t border-red-200 pt-1">
              <span>Shortfall:</span>
              <span className="font-semibold">{formatPence(shortfall)}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onAddFunds}
            className="mt-3 text-sm font-medium text-red-600 underline hover:text-red-800"
          >
            Add Funds
          </button>
        </div>
      </div>
    </div>
  );
}

interface BalanceDisplayProps {
  currentBalancePence: number;
}

/**
 * Simple balance display for payment modals.
 */
export function BalanceDisplay({ currentBalancePence }: BalanceDisplayProps) {
  return (
    <div className="flex justify-between rounded-lg bg-gray-50 px-4 py-2 text-sm">
      <span className="text-gray-600">Your balance:</span>
      <span className="font-medium text-gray-900">{formatPence(currentBalancePence)}</span>
    </div>
  );
}
